// @ts-check

import { DIFFICULTIES } from "../engine/constants.js";

const field = (type, options = {}) => Object.freeze({ type, ...options });

export const ACTION_CAPABILITIES = Object.freeze({
  start_puzzle: capability(
    "Start or resume a published puzzle.",
    "both",
    {
      puzzle_id: field("id", { required: true, maxLength: 128 }),
      daily_date: field("date"),
      plan_hash: field("string", { maxLength: 80 }),
    },
    { candidates: true, planning: "conditional" },
  ),
  place_digit: capability(
    "Enter a digit in an editable cell.",
    "both",
    {
      cell: field("cell", { required: true }),
      digit: field("integer", { required: true, minimum: 1, maximum: 9 }),
    },
    { candidates: true },
  ),
  toggle_candidate_note: capability(
    "Toggle a candidate note in an editable cell.",
    "both",
    {
      cell: field("cell", { required: true }),
      digit: field("integer", { required: true, minimum: 1, maximum: 9 }),
    },
    { candidates: true },
  ),
  erase_cell: capability("Clear an editable cell and its notes.", "both", {
    cell: field("cell", { required: true }),
  }),
  undo: capability("Undo the latest board edit.", "both"),
  redo: capability("Redo the latest undone board edit.", "both"),
  request_hint: capability(
    "Open the first stage of a supported logical hint.",
    "both",
  ),
  advance_hint: capability("Show the next stage of the current hint.", "both"),
  dismiss_hint: capability(
    "Dismiss the current hint without applying it.",
    "both",
  ),
  apply_hint: capability(
    "Apply the fully revealed logical hint.",
    "both",
    {
      plan_hash: field("string", { maxLength: 80 }),
    },
    { planning: "required" },
  ),
  reveal_cell: capability(
    "Reveal the solution digit for one editable cell.",
    "both",
    {
      cell: field("cell", { required: true }),
      plan_hash: field("string", { maxLength: 80 }),
    },
    { planning: "required" },
  ),
  pause_game: capability("Pause active-time tracking.", "both"),
  resume_game: capability("Resume active-time tracking.", "both"),
  set_gameplay_options: capability(
    "Change options that affect authoritative moves.",
    "both",
    {
      check_against_solution: field("boolean"),
      auto_remove_peer_notes: field("boolean"),
    },
  ),
  advance_time: capability(
    "Advance the authoritative active timer exactly.",
    "both",
    {
      milliseconds: field("integer", {
        required: true,
        minimum: 0,
        maximum: 2000,
      }),
    },
  ),
  navigate: capability("Navigate between graphical screens.", "human", {
    route: field("string", { required: true, maxLength: 128 }),
  }),
  select_cell: capability("Move graphical focus to a cell.", "human", {
    cell: field("cell", { required: true }),
  }),
  set_note_mode: capability(
    "Choose graphical digit or note input mode.",
    "human",
    {
      enabled: field("boolean", { required: true }),
    },
  ),
  set_presentation_preferences: capability(
    "Change display, audio, companion, or accessibility preferences.",
    "human",
    {
      preferences: field("object", { required: true, maxProperties: 20 }),
    },
  ),
  open_dialog: capability("Open a graphical confirmation dialog.", "human", {
    dialog: field("enum", {
      required: true,
      values: ["pause", "reset_statistics"],
    }),
  }),
  close_dialog: capability("Close the active graphical dialog.", "human"),
  share_result: capability(
    "Open the platform result-sharing interface.",
    "human",
    {
      puzzle_id: field("id", { required: true, maxLength: 128 }),
    },
  ),
  reset_statistics: capability(
    "Hide recorded completions from statistics.",
    "human",
    {
      plan_hash: field("string", { maxLength: 80 }),
    },
    { planning: "required" },
  ),
});

export const OBSERVATION_REGISTRY = Object.freeze({
  summary: observation("Current session, phase, puzzle, and timer."),
  objectives: observation(
    "Visible solve, medal, collectible, pack, and collection goals.",
  ),
  board: observation("The bounded 9 by 9 player-visible board."),
  hint: observation("Only the currently revealed hint stage."),
  progression: observation("Collectible and pack progress."),
  collection: observation("Published collectibles and unlock status.", true),
  catalog: observation("Published puzzles available to start.", true),
  statistics: observation("Aggregated saved results."),
  settings: observation("Current gameplay settings."),
  recent_events: observation("Bounded public events.", true),
  history: observation("Bounded accepted action receipts.", true),
});

export const SESSION_MODES = Object.freeze([
  "journey",
  "puzzle",
  "daily",
  "quick_play",
]);
export const STOP_CLASSES = Object.freeze([
  "decision_required",
  "critical_event",
  "milestone",
  "important_result",
  "victory",
  "defeat",
]);
export const API_LIMITS = Object.freeze({
  observation_requests: 8,
  catalog_page: 24,
  public_events: 50,
  stored_events: 200,
  action_batch: 8,
  response_bytes: 512 * 1024,
  save_bytes: 1024 * 1024,
  advance_steps: 3600,
  session_advance_steps: 86400,
});

export function validateActionShape(action, exposure = "agent", options = {}) {
  if (!action || typeof action !== "object" || Array.isArray(action))
    return failure("INVALID_ACTION", "Action must be an object.");
  if (typeof action.type !== "string")
    return failure("INVALID_ACTION_TYPE", "Action type must be a string.");
  const definition = ACTION_CAPABILITIES[action.type];
  if (!definition)
    return failure("UNKNOWN_ACTION", `Unknown action type: ${action.type}.`, {
      action_type: action.type,
    });
  if (exposure === "agent" && definition.exposure === "human")
    return failure(
      "ACTION_NOT_EXPOSED",
      `${action.type} is available only to the human interface.`,
      { action_type: action.type },
    );
  const allowed = new Set(["type", ...Object.keys(definition.fields)]);
  if (options.draft) allowed.add("plan_hash");
  for (const key of Object.keys(action))
    if (!allowed.has(key))
      return failure(
        "UNKNOWN_FIELD",
        `Unknown field ${key} for ${action.type}.`,
        { field: key },
      );
  for (const [name, schema] of Object.entries(definition.fields)) {
    if (!(name in action)) {
      if (schema.required && !(options.draft && name === "plan_hash"))
        return failure("MISSING_FIELD", `Missing required field ${name}.`, {
          field: name,
        });
      continue;
    }
    const error = validateValue(action[name], schema);
    if (error)
      return failure(error.code, `${name}: ${error.message}`, {
        field: name,
        value: action[name],
      });
  }
  if (
    action.type === "set_gameplay_options" &&
    !("check_against_solution" in action) &&
    !("auto_remove_peer_notes" in action)
  )
    return failure(
      "INVALID_COMBINATION",
      "At least one gameplay option is required.",
    );
  return { ok: true, definition };
}

export function cellIdToIndex(value) {
  const match =
    typeof value === "string" ? /^r([1-9])c([1-9])$/.exec(value) : null;
  return match ? (Number(match[1]) - 1) * 9 + Number(match[2]) - 1 : -1;
}

export function indexToCellId(index) {
  return `r${Math.floor(index / 9) + 1}c${(index % 9) + 1}`;
}

export function publicCapabilityDescriptors(exposure = "agent") {
  return Object.entries(ACTION_CAPABILITIES)
    .filter(([, item]) => exposure !== "agent" || item.exposure !== "human")
    .map(([type, item]) => ({
      type,
      ...structuredClone(item),
      example: exampleFor(type, item),
    }));
}

function capability(purpose, exposure, fields = {}, extras = {}) {
  return Object.freeze({
    purpose,
    exposure,
    fields: Object.freeze(fields),
    candidates: false,
    planning: "none",
    ...extras,
  });
}
function observation(purpose, paginated = false) {
  return Object.freeze({ purpose, paginated });
}
function failure(code, message, evidence = {}) {
  return { ok: false, error: { code, message, evidence } };
}
function validateValue(value, schema) {
  if (
    schema.type === "string" ||
    schema.type === "id" ||
    schema.type === "date" ||
    schema.type === "cell" ||
    schema.type === "enum"
  ) {
    if (typeof value !== "string")
      return { code: "INVALID_TYPE", message: "must be a string" };
    if (schema.maxLength && value.length > schema.maxLength)
      return {
        code: "OUT_OF_RANGE",
        message: `must be at most ${schema.maxLength} characters`,
      };
    if (schema.type === "id" && !/^[a-z0-9_]+$/.test(value))
      return { code: "INVALID_ID", message: "must be a stable snake-case ID" };
    if (schema.type === "date" && !/^\d{4}-\d{2}-\d{2}$/.test(value))
      return { code: "INVALID_DATE", message: "must be YYYY-MM-DD" };
    if (schema.type === "cell" && cellIdToIndex(value) < 0)
      return { code: "INVALID_CELL", message: "must be r1c1 through r9c9" };
    if (schema.type === "enum" && !schema.values.includes(value))
      return {
        code: "INVALID_ENUM",
        message: `must be one of ${schema.values.join(", ")}`,
      };
  } else if (schema.type === "integer") {
    if (!Number.isInteger(value) || !Number.isFinite(value))
      return { code: "INVALID_NUMBER", message: "must be a finite integer" };
    if (value < schema.minimum || value > schema.maximum)
      return {
        code: "OUT_OF_RANGE",
        message: `must be between ${schema.minimum} and ${schema.maximum}`,
      };
  } else if (schema.type === "boolean") {
    if (typeof value !== "boolean")
      return { code: "INVALID_TYPE", message: "must be a boolean" };
  } else if (schema.type === "object") {
    if (!value || typeof value !== "object" || Array.isArray(value))
      return { code: "INVALID_TYPE", message: "must be an object" };
    if (Object.keys(value).length > schema.maxProperties)
      return { code: "OUT_OF_RANGE", message: "contains too many properties" };
  }
  return null;
}
function exampleFor(type, item) {
  const output = { type };
  for (const [name, schema] of Object.entries(item.fields)) {
    if (!schema.required || name === "plan_hash") continue;
    output[name] =
      schema.type === "cell"
        ? "r1c1"
        : schema.type === "integer"
          ? schema.minimum
          : schema.type === "boolean"
            ? true
            : schema.type === "object"
              ? {}
              : (schema.values?.[0] ??
                (schema.type === "date" ? "2026-09-05" : "example_id"));
  }
  return output;
}

export { DIFFICULTIES };
